import { ChapterDTO } from '@/app/models/series.model';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

interface ChapterAuthor {
  id?: number;
  name?: string;
  nameFirst?: string;
  nameLast?: string;
  title?: string;
  orcid?: string;
  email?: string;
  institutionName?: string;
  institutionAddress?: string;
  country?: string;
  websiteUrl?: string;
}


@Component({
    selector: 'app-chapter-view',
    templateUrl: './chapter-view.component.html',
    styleUrls: ['./chapter-view.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChapterViewComponent {
  @Input() dataList: ChapterDTO[] = [];
  @Input() count: number = 10;
  @Input() col: number = 1;
  @Input() includeAbstract: boolean = false;
  @Input() parentSlug?: string;

  constructor(private router: Router) {}

  ngOnInit() {
    // Component initialization logic if needed
  }

  /**
   * Get the chapters to display based on the count limit
   */
  getDisplayChapters(): ChapterDTO[] {
    return this.dataList.slice(0, this.count);
  }

  /**
   * Get the column class for Bootstrap grid
   */
  getColumnClass(): string {
    const colSize = 12 / (this.col || 1);
    return `col-md-${colSize}`;
  }

  /**
   * Navigate to chapter detail page
   */
  navigateToChapter(chapter: ChapterDTO) {
    if (chapter.slug && chapter.bookSlug) {
      this.router.navigate(['/book', chapter.bookSlug, 'chapter', chapter.slug]);
    }
  }

  /**
   * Get formatted abstract with proper HTML ending
   */
  getFormattedAbstract(abstract: string): string {
    if (!abstract) return '';
    
    let formattedAbstract = abstract + '...';
    
    // Add proper closing tag based on the opening tag
    if (abstract.indexOf('<div>') === 0) {
      formattedAbstract += '</div>';
    } else {
      formattedAbstract += '</p>';
    }
    
    return `<div class="from-journal-content text-justify">${formattedAbstract}</div>`;
  }

  /**
   * Get author display name
   */
  getAuthorDisplayName(author: ChapterAuthor): string {
    if (author.nameFirst && author.nameLast) {
      return `${author.nameFirst} ${author.nameLast}`;
    }
    return author.name || 'Unknown Author';
  }  

  /**
   * Get language code from locale text
   */
  getLanguageCode(localeText: string): string {
    if (!localeText) return '';
    return localeText.split('_')[0].toUpperCase();
  }

  /**
   * Check if chapter has abstract
   */
  hasAbstract(chapter: ChapterDTO): boolean {
    return !!(chapter.abstract && chapter.abstract.trim());
  }


  /**
   * Check if chapter has DOI
   */
  hasDoi(chapter: ChapterDTO): boolean {
    return !!(chapter.doi && chapter.doi.trim());
  }

  /**
   * Get DOI URL
   */
  getDoiUrl(doi: string): string {
    if (!doi) return '';
    return `https://dx.doi.org/${doi}`;
  }

  /**
   * Check if chapter has page numbers
   */
  hasPageNumbers(chapter: ChapterDTO): boolean {
    return !!(chapter.firstPage && chapter.lastPage);
  }

  /**
   * Check if this is the last item in the list (for HR separator)
   */
  isLastItem(index: number): boolean {
    return index >= (this.count - 1);
  }

  /**
   * Get formatted page range
   */
  getPageRange(chapter: ChapterDTO): string {
    if (!this.hasPageNumbers(chapter)) return '';
    return `${chapter.firstPage}-${chapter.lastPage}`;
  }

  /**
   * Check if should show abstract based on includeAbstract setting
   */
  shouldShowAbstract(chapter: ChapterDTO): boolean {
    return this.includeAbstract && this.hasAbstract(chapter);
  }

  /**
   * Get chapter display title with number if available
   */
  getChapterDisplayTitle(chapter: ChapterDTO): string {
    if (chapter.eid) {
      return `${chapter.eid}. ${chapter.title}`;
    }
    return chapter.title || '';
  }
}
